// Invitation landing page (/accept-invite?token=…).
//
// Where an invitee lands after clicking the magic link in their invite email. By
// the time this renders the user is authenticated (Supabase processed the magic
// link) and — because handle_login auto-accepts any pending invitation addressed to
// the verified email on every request — already approved and a member of the
// inviting org (their initial /iam/me call did it server-side). So this page just
// confirms, then drops them into the app. The explicit token redemption is a
// belt-and-braces fallback; "already used" / auto-accepted is treated as success.
//
// IMPORTANT (loop guard): this page lives behind <AuthGate>, which renders a global
// spinner whenever AuthContext.loading is true and otherwise the router. Calling
// refresh() here would flip loading true→false, UNMOUNT this route, then remount it
// — re-running the effect and hammering /invitations/accept forever. So we (1) do NOT
// call refresh() (the invited user's org is already in context from the initial /me),
// and (2) guard the effect with a module-level set so a remount can't re-run it.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import iamApi from '../services/iamApi';

// Tokens already processed in this page load — survives the route remounts that
// AuthGate's loading flip can cause, so the accept call fires exactly once.
const processedTokens = new Set();

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { chooseOrg } = useAuth();
  const [state, setState] = useState('working'); // working | done | error
  const [detail, setDetail] = useState('');
  const token = params.get('token') || '';

  // `chooseOrg` and `navigate` are CALLED by the effect below but must never be able to
  // re-trigger it. Neither is contractually stable (react-router rebuilds `navigate` on
  // some location changes; `chooseOrg` comes from AuthContext), and a re-entry is not
  // merely wasteful here — the module-level `processedTokens` guard answers a repeat run
  // by flipping state 'working' -> 'done', which while the accept is still IN FLIGHT
  // would show the success card before the invitation had actually been redeemed.
  // Latest-value refs keep the trigger set to `token` alone: a genuinely different
  // invite token is the only thing that should start this over.
  const chooseOrgRef = useRef(chooseOrg);
  const navigateRef = useRef(navigate);
  useEffect(() => {
    chooseOrgRef.current = chooseOrg;
    navigateRef.current = navigate;
  }, [chooseOrg, navigate]);

  useEffect(() => {
    const key = token || '(none)';
    if (processedTokens.has(key)) {
      // Already handled this page load (likely a remount). Don't re-run; if we
      // finished successfully we'd have navigated away, so just show the done card.
      setState((s) => (s === 'working' ? 'done' : s));
      return undefined;
    }
    processedTokens.add(key);
    let cancelled = false;
    (async () => {
      try {
        if (token) {
          try {
            const r = await iamApi.acceptInvite(token);
            if (r?.org_id) chooseOrgRef.current(r.org_id);
          } catch (e) {
            const msg = e?.response?.data?.detail || '';
            if (/different email/i.test(msg)) {
              if (!cancelled) { setDetail(msg); setState('error'); }
              return;
            }
            // Benign: already auto-accepted / used. The membership is already in
            // place from the initial /me; fall through to success.
          }
        }
        if (!cancelled) {
          setState('done');
          // Hand off to the app. AuthContext already holds the membership (auto-accept
          // ran in the initial /me), so no refresh() is needed — and calling it here
          // would remount this route and loop. replace: no /accept-invite in history.
          navigateRef.current('/', { replace: true });
        }
      } catch {
        if (!cancelled) { setDetail('Could not complete the invitation.'); setState('error'); }
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', p: 2 }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: 460, width: '100%', textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>Join your organisation</Typography>
        {state === 'working' && (
          <Stack spacing={2} sx={{ py: 2, alignItems: 'center' }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Connecting you to your organisation…</Typography>
          </Stack>
        )}
        {state === 'done' && (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>You&apos;re in. Your account is approved and added to the organisation.</Alert>
            <Button variant="contained" onClick={() => navigate('/', { replace: true })}>Go to RMDV</Button>
          </>
        )}
        {state === 'error' && (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {detail || 'This invitation could not be completed.'}{' '}
              {/different email/i.test(detail) && 'Sign in with the email address the invitation was sent to.'}
            </Alert>
            <Button variant="outlined" onClick={() => navigate('/', { replace: true })}>Continue</Button>
          </>
        )}
      </Paper>
    </Box>
  );
}
